import { useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { IMAGES, TerminalImage } from "./TerminalImage";

const FACILITY_TYPES = [
  "CRE Senior Debt",
  "Construction Mezzanine",
  "Corporate Term Facility",
  "Other / Not Sure",
];

const inputCls =
  "w-full p-2.5 rounded bg-navy border border-line text-white placeholder:text-slate-600 focus:border-cy outline-none transition-colors";

const initialForm = {
  entity: "",
  contact_name: "",
  email: "",
  loan_amount: "",
  facility_type: FACILITY_TYPES[0],
  message: "",
};

export const EnquirySection = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [newsEmail, setNewsEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry transmitted. Grow Capital will be in touch.");
      setForm(initialForm);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Transmission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      const r = await api.post("/newsletter", { email: newsEmail });
      if (r.data.status === "already_subscribed") {
        toast.info("This address is already on the brief list.");
      } else {
        toast.success("Subscribed to the monthly private credit brief.");
      }
      setNewsEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Subscription failed. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section id="terminal-enquiry" className="py-20 border-b border-line max-w-6xl mx-auto px-6" data-testid="enquiry-section">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 bg-surface p-8 rounded-2xl border border-line">
          <span className="text-xs font-mono text-cy uppercase tracking-widest">[PORTAL] BORROWER ROUTING</span>
          <h3 className="font-display text-3xl text-white uppercase mt-2">Submit Funding Enquiry</h3>
          <p className="text-xs text-mut mt-1 mb-6 font-body">
            Commercial real estate, corporate facilities and construction financing routed to Grow Capital.
          </p>

          <form className="space-y-4 text-xs font-mono" onSubmit={submitEnquiry} data-testid="borrower-enquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="enquiry-entity" className="block text-mut mb-1">BORROWER ENTITY</label>
                <input
                  id="enquiry-entity"
                  type="text"
                  required
                  value={form.entity}
                  onChange={set("entity")}
                  placeholder="Entity Pty Ltd"
                  data-testid="enquiry-entity-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-contact" className="block text-mut mb-1">CONTACT NAME</label>
                <input
                  id="enquiry-contact"
                  type="text"
                  required
                  value={form.contact_name}
                  onChange={set("contact_name")}
                  placeholder="Full name"
                  data-testid="enquiry-contact-input"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="enquiry-email" className="block text-mut mb-1">CONTACT EMAIL</label>
                <input
                  id="enquiry-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="borrower@domain.com.au"
                  data-testid="enquiry-email-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-amount" className="block text-mut mb-1">LOAN AMOUNT ($ AUD)</label>
                <input
                  id="enquiry-amount"
                  type="text"
                  required
                  value={form.loan_amount}
                  onChange={set("loan_amount")}
                  placeholder="$5M - $50M+"
                  data-testid="enquiry-amount-input"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label htmlFor="enquiry-facility" className="block text-mut mb-1">FACILITY TYPE</label>
              <select
                id="enquiry-facility"
                value={form.facility_type}
                onChange={set("facility_type")}
                data-testid="enquiry-facility-select"
                className={inputCls}
              >
                {FACILITY_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="enquiry-message" className="block text-mut mb-1">PROJECT NOTES (OPTIONAL)</label>
              <textarea
                id="enquiry-message"
                rows={3}
                value={form.message}
                onChange={set("message")}
                placeholder="Location, security, timing&hellip;"
                data-testid="enquiry-message-input"
                className={`${inputCls} resize-y`}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="w-full py-3 bg-cy text-navy font-bold rounded hover:bg-cy-soft transition-colors mt-2 disabled:opacity-60"
            >
              {sending ? "TRANSMITTING\u2026" : "TRANSMIT TO GROW CAPITAL"}
            </button>
          </form>
        </div>

        <div id="terminal-news" className="lg:col-span-5 flex flex-col justify-between">
          <div className="bg-surface p-8 rounded-2xl border border-line">
            <span className="text-xs font-mono text-cy uppercase tracking-widest">[DISPATCH] MONTHLY BRIEF</span>
            <h3 className="font-display text-3xl text-white uppercase mt-2">Private Credit Briefing</h3>
            <p className="text-xs text-mut mt-2 mb-6 font-body">
              Synthesising Australian market moves, regulatory actions and manager data straight to your inbox.
            </p>

            <form className="space-y-3 text-xs font-mono" onSubmit={submitNewsletter} data-testid="newsletter-form">
              <input
                type="email"
                required
                value={newsEmail}
                onChange={(e) => setNewsEmail(e.target.value)}
                placeholder="name@institution.com.au"
                aria-label="Newsletter email"
                data-testid="newsletter-email-input"
                className={inputCls}
              />
              <button
                type="submit"
                disabled={subscribing}
                data-testid="newsletter-submit-button"
                className="w-full py-2.5 bg-white text-navy font-bold rounded hover:bg-slate-200 transition-colors disabled:opacity-60"
              >
                {subscribing ? "SUBSCRIBING\u2026" : "SUBSCRIBE TO BRIEF"}
              </button>
            </form>
          </div>

          <div className="mt-6 rounded-xl overflow-hidden border border-line">
            <TerminalImage
              src={IMAGES.newsletter}
              alt="Encrypted intelligence document"
              testId="newsletter-visual"
              className="aspect-[16/6] drift-cyan p-4"
            >
              <span className="text-[10px] font-mono text-cy bg-navy/90 px-2 py-0.5 rounded w-max border border-cy/30 mt-auto">
                MONTHLY AUDIT SUMMARY
              </span>
            </TerminalImage>
          </div>
        </div>
      </div>
    </section>
  );
};
